// Reads a spreadsheet the user picked (CSV or Excel .xlsx) into rows of text — all in the browser, no upload.
// .xlsx is a zip of XML files; we unzip with the browser's own DecompressionStream and read the first sheet.

export type Sheet = { headers: string[]; rows: string[][] };

export async function readSheet(file: File): Promise<Sheet> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xls')) throw new Error('Old Excel (.xls) file — open it in Excel and "Save As" .xlsx or .csv, then try again.');
  const grid = name.endsWith('.xlsx') ? await readXlsx(await file.arrayBuffer()) : parseCsv(await file.text());
  // Some exports put a title line or blank lines above the real header: take the first row with 2+ filled cells.
  const start = grid.findIndex(r => r.filter(c => c.trim()).length >= 2);
  if (start < 0) throw new Error('The file looks empty.');
  const headers = grid[start].map((h, i) => h.trim() || `Column ${i + 1}`);
  const rows = grid.slice(start + 1).filter(r => r.some(c => c.trim())).map(r => headers.map((_, i) => (r[i] ?? '').trim()));
  return { headers, rows };
}

export function parseCsv(text: string): string[][] {
  const t = text.replace(/^﻿/, '');
  const delim = (t.split('\n')[0].match(/\t/g) || []).length > (t.split('\n')[0].match(/,/g) || []).length ? '\t' : t.split('\n')[0].includes(';') && !t.split('\n')[0].includes(',') ? ';' : ',';
  const out: string[][] = [];
  let row: string[] = [], cell = '', q = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (q) {
      if (ch === '"') { if (t[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += ch;
    } else if (ch === '"') { q = true; if (cell === '=') cell = ''; } // Excel-style ="00123" keeps leading zeros — we just want 00123
    else if (ch === delim) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && t[i + 1] === '\n') i++; row.push(cell); out.push(row); row = []; cell = ''; }
    else cell += ch;
  }
  if (cell || row.length) { row.push(cell); out.push(row); }
  return out;
}

async function unzip(buf: ArrayBuffer): Promise<Map<string, Uint8Array>> {
  const v = new DataView(buf), u8 = new Uint8Array(buf);
  let eocd = -1;
  for (let i = buf.byteLength - 22; i >= Math.max(0, buf.byteLength - 66000); i--) if (v.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("This isn't a readable .xlsx file. Save it as CSV and try again.");
  const n = v.getUint16(eocd + 10, true);
  let p = v.getUint32(eocd + 16, true);
  const files = new Map<string, Uint8Array>();
  const dec = new TextDecoder();
  for (let k = 0; k < n; k++) {
    const method = v.getUint16(p + 10, true), size = v.getUint32(p + 20, true);
    const nameLen = v.getUint16(p + 28, true), extra = v.getUint16(p + 30, true), comment = v.getUint16(p + 32, true);
    const local = v.getUint32(p + 42, true);
    const name = dec.decode(u8.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extra + comment;
    if (!/^xl\/(sharedStrings\.xml|workbook\.xml|worksheets\/sheet\d+\.xml|_rels\/workbook\.xml\.rels)$/.test(name)) continue;
    const start = local + 30 + v.getUint16(local + 26, true) + v.getUint16(local + 28, true);
    const data = u8.subarray(start, start + size);
    if (method === 0) files.set(name, data);
    else if (method === 8) {
      if (typeof DecompressionStream === 'undefined') throw new Error('This browser cannot open Excel files — save as CSV, or use Chrome.');
      const ds = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
      files.set(name, new Uint8Array(await new Response(ds).arrayBuffer()));
    }
  }
  return files;
}

async function readXlsx(buf: ArrayBuffer): Promise<string[][]> {
  const files = await unzip(buf);
  const dec = new TextDecoder();
  const xml = (n: string) => (files.has(n) ? new DOMParser().parseFromString(dec.decode(files.get(n)), 'application/xml') : null);
  const shared: string[] = [];
  const ss = xml('xl/sharedStrings.xml');
  if (ss) for (const si of Array.from(ss.getElementsByTagName('si'))) shared.push(Array.from(si.getElementsByTagName('t')).map(t => t.textContent || '').join(''));
  // First sheet in the workbook order.
  let sheetPath = 'xl/worksheets/sheet1.xml';
  const wb = xml('xl/workbook.xml'), rels = xml('xl/_rels/workbook.xml.rels');
  const firstId = wb?.getElementsByTagName('sheet')[0]?.getAttribute('r:id');
  const target = firstId && Array.from(rels?.getElementsByTagName('Relationship') || []).find(r => r.getAttribute('Id') === firstId)?.getAttribute('Target');
  if (target) sheetPath = 'xl/' + target.replace(/^\/?xl\//, '').replace(/^\//, '');
  const sh = xml(sheetPath) || xml('xl/worksheets/sheet1.xml');
  if (!sh) throw new Error('No sheet found in this Excel file.');
  const out: string[][] = [];
  for (const r of Array.from(sh.getElementsByTagName('row'))) {
    const ri = Number(r.getAttribute('r') || out.length + 1) - 1;
    const row: string[] = [];
    for (const c of Array.from(r.getElementsByTagName('c'))) {
      const ref = c.getAttribute('r') || '';
      const col = ref.replace(/\d+/g, '').split('').reduce((a, ch) => a * 26 + ch.charCodeAt(0) - 64, 0) - 1;
      const t = c.getAttribute('t');
      const val = c.getElementsByTagName('v')[0]?.textContent ?? '';
      const text = t === 's' ? shared[Number(val)] ?? '' : t === 'inlineStr' ? Array.from(c.getElementsByTagName('t')).map(x => x.textContent || '').join('') : t === 'b' ? (val === '1' ? 'TRUE' : 'FALSE') : val;
      row[col >= 0 ? col : row.length] = text;
    }
    out[ri] = Array.from(row, x => x ?? '');
  }
  return Array.from(out, r => r ?? []);
}

export const isSheet = (f: File) => /\.(xlsx|xls|csv|tsv)$/i.test(f.name) || /spreadsheetml|csv/.test(f.type);
/** The sheet as plain text for the AI: "cell | cell | cell", header first. */
export const sheetText = (s: Sheet) => [s.headers, ...s.rows].slice(0, 400).map(r => r.join(' | ')).join('\n');
